import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFinancialData, useClientsData, useReservationsData, clearFinancialCache } from '../../services/dataService';
import config from '../../config';
import { useSettings } from '../../contexts/SettingsContext';
import TransactionDialog from '../../components/finances/TransactionDialog';
import TransactionCard from '../../components/finances/TransactionCard';
import FilterBar from '../../components/common/FilterBar';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  Avatar,
  Grow,
  useTheme,
  useMediaQuery,
  Badge,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TablePagination,
  Tooltip
} from '@mui/material';

// Icons
import {
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  BarChart,
  ReceiptLong,
  Paid,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  Add as AddIcon,
  FilterAlt as FilterAltIcon,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';

// Define simple SVG icons as components
const ArrowTrendingUpIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 18 8.25-8.25 4.5 4.5 6-6" />
  </svg>
);

const ArrowTrendingDownIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 6 8.25 8.25 4.5-4.5 6 6" />
  </svg>
);

const BanknotesIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
  </svg>
);

const ClockIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const PlusIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const DocumentArrowDownIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);

const ArrowPathIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const FunnelIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
  </svg>
);

// Components

const FinancesPage = () => {
  const { settings, formatDate, formatCurrency } = useSettings();
  const { apiUrl } = config;
  
  // Clear any existing financial transactions on load
  useEffect(() => {
    fetch(`${apiUrl}/finances/transactions`, { method: 'GET' })
      .then(() => console.log('Finance data checked'))
      .catch(err => console.error('Error checking finance data:', err));
  }, []);
  // Data hooks
  const { financialData: transactions, loading } = useFinancialData();
  const { clients } = useClientsData();
  const { reservations } = useReservationsData();
  
  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all'); // Pour filtration rapide
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const showQuickActions = false;
  
  // State for transactions
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  
  // State for transaction dialog
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  // Filter transactions when filters change
  useEffect(() => {
    if (!transactions) return;
    
    let filtered = [...transactions];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.client?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(t => t.type === typeFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.statut === statusFilter);
    }
    
    // Apply period filter
    if (periodFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (periodFilter) {
        case 'today':
          filtered = filtered.filter(t => new Date(t.date) >= today);
          break;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          filtered = filtered.filter(t => new Date(t.date) >= weekAgo);
          break;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          filtered = filtered.filter(t => new Date(t.date) >= monthAgo);
          break;
        default:
          break;
      }
    }
    
    // Apply filter from quick filter tabs
    if (activeTab !== 'all') {
      switch (activeTab) {
        case 'recettes':
          filtered = filtered.filter(t => t.type === 'recette');
          break;
        case 'depenses':
          filtered = filtered.filter(t => t.type === 'depense');
          break;
        case 'payes':
          filtered = filtered.filter(t => t.statut === 'payé');
          break;
        case 'attente':
          filtered = filtered.filter(t => t.statut === 'en attente');
          break;
        case 'thisMonth':
          const now = new Date();
          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          filtered = filtered.filter(t => new Date(t.date) >= firstDayOfMonth);
          break;
        default:
          break;
      }
    }
    
    setFilteredTransactions(filtered);
  }, [transactions, searchTerm, typeFilter, statusFilter, periodFilter, activeTab]);
  
  // Pagination for card view
  const paginatedTransactions = filteredTransactions.slice(0, 50); // Show first 50 transactions for performance

  // Handler functions for transaction actions are defined below
  
  // Calculate summary stats
  const financialSummary = React.useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return {
        recettes: { total: 0, count: 0 },
        depenses: { total: 0, count: 0 },
        benefice: { total: 0, margin: 0 },
        enAttente: { total: 0, count: 0 }
      };
    }
    
    // Calculate revenues
    const recettesTotal = transactions
      .filter(t => t.type === 'Recette')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
    
    // Calculate expenses
    const depensesTotal = transactions
      .filter(t => t.type === 'Dépense')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
    
    // Calculate profit and margin
    const beneficeTotal = recettesTotal - depensesTotal;
    const margin = recettesTotal > 0 ? (beneficeTotal / recettesTotal) * 100 : 0;
    
    // Calculate pending payments
    const enAttenteTotal = transactions
      .filter(t => t.statut === 'En attente')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
    
    return {
      recettes: {
        total: recettesTotal,
        count: transactions.filter(t => t.type === 'Recette').length
      },
      depenses: {
        total: depensesTotal,
        count: transactions.filter(t => t.type === 'Dépense').length
      },
      benefice: {
        total: beneficeTotal,
        margin: margin
      },
      enAttente: {
        total: enAttenteTotal,
        count: transactions.filter(t => t.statut === 'En attente').length
      }
    };
  }, [transactions]);

  // Open dialog for new transaction
  const handleNewTransaction = () => {
    setEditingTransaction(null);
    setTransactionDialogOpen(true);
  };
  
  // Handle transaction save
  const handleSaveTransaction = (savedTransaction) => {
    // In a real app, you might refresh data or update local state
    console.log('Transaction saved:', savedTransaction);
    
    // Clear cache to force refresh
    clearFinancialCache();
    
    // Refresh financial data (this would happen automatically with real-time hooks)
    setTimeout(() => {
      window.forceFinancialRefresh = true;
      window.dispatchEvent(new CustomEvent('financialDataUpdate'));
    }, 100);
  };
  // Handle view transaction
  const handleViewTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionDialogOpen(true);
  };
  
  // Open dialog to edit transaction
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionDialogOpen(true);
  };
  
  // Handle transaction deletion
  const handleDeleteTransaction = (transaction) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer cette transaction : ${transaction.description || transaction.reference || transaction.id} ?`)) {
      // Optimistic UI update - remove transaction immediately from filtered view
      setFilteredTransactions(prevTransactions => 
        prevTransactions.filter(t => t.id !== transaction.id)
      );
      
      try {
        // Use apiCall with proper error handling
        fetch(`${apiUrl}/finances/transactions/${transaction.id}`, {
          method: 'DELETE',
        })
          .then(response => {
            // Success or error, clear cache to force global refresh
            clearFinancialCache();
            window.forceFinancialRefresh = true;
            window.dispatchEvent(new CustomEvent('financialDataUpdate'));
            
            // Check for error response but don't disrupt UX
            if (!response.ok) {
              console.warn('Backend reported issue with deletion:', response.status);
            }
          })
          .catch(error => {
            console.error('Transaction deletion network error:', error);
            // Continue to dispatch update even on error
            window.dispatchEvent(new CustomEvent('financialDataUpdate'));
          });
      } catch (error) {
        console.error('Transaction deletion error:', error);
        // Even on error, keep the transaction removed for better UX
        // We'll refresh data in background to stay consistent with server
        window.dispatchEvent(new CustomEvent('financialDataUpdate'));
      }
    }
  };
  
  // Transaction status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Terminé':
        return 'badge-success';
      case 'En attente':
        return 'badge-warning';
      case 'Annulé':
        return 'badge-danger';
      default:
        return 'badge-info';
    }
  };

  // Define LoadingIndicator component
  const LoadingIndicator = () => (
    <Box sx={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}>
      <CircularProgress color="primary" size={60} />
      <Typography variant="h6" sx={{ color: 'rgba(51,65,85,0.8)' }}>
        Chargement des données financières...
      </Typography>
    </Box>
  );

  // Define EnhancedStatCard component from Dashboard
  const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
    <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
          color: gradient ? 'white' : 'inherit',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          } : {},
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mb: 0.5, 
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.875rem' }
              }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' }
              }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ 
                  opacity: gradient ? 0.9 : 0.7, 
                  mt: 0.5,
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip 
                  label={trend} 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: trend.startsWith('+') ? 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                    color: gradient ? 'white' : 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    height: { xs: 20, md: 24 }
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
                color: gradient ? 'white' : 'primary.main',
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 }
              }}
            >
              {icon}
            </Avatar>
          </Box>
          
          {progress && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  Progression
                </Typography>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: gradient ? 'white' : 'primary.main',
                  }
                }} 
              />
            </Box>
          )}
          
          {chart && (
            <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
              {chart}
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );

  // Define FloatingActionCard component
  const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
    return (
      <Card sx={{
        background: `linear-gradient(135deg, ${color}15, white)`,
        border: `1px solid ${color}30`,
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        height: '100%', // Ensure consistent height
      }} onClick={onClick}>
        <CardContent sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 }, 
          position: 'relative', 
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1, md: 2 }, 
            mb: { xs: 1.5, md: 2 },
            flexWrap: 'wrap'
          }}>
            <Avatar className="action-icon" sx={{ 
              background: `linear-gradient(135deg, ${color}, ${color}cc)`,
              width: { xs: 48, sm: 52, md: 56 },
              height: { xs: 48, sm: 52, md: 56 },
              boxShadow: `0 8px 25px ${color}40`,
              '& .MuiSvgIcon-root': {
                fontSize: { xs: 26, sm: 28, md: 30 }
              }
            }}>
              {icon}
            </Avatar>
            {badge && (
              <Chip 
                label={badge} 
                size="small" 
                sx={{ 
                  background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                  color: 'white',
                  fontWeight: 600,
                  height: { xs: '20px', md: '24px' },
                  fontSize: { xs: '0.7rem', md: '0.75rem' }
                }}
              />
            )}
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              mb: { xs: 0.5, md: 1 }, 
              color: 'rgba(51,65,85,0.9)',
              lineHeight: 1.3,
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(51,65,85,0.7)',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              display: { xs: '-webkit-box', md: 'block' },
              WebkitLineClamp: { xs: 2, md: 'none' },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to get status color
  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    switch (status.toLowerCase()) {
      case 'confirmé':
      case 'complété':
      case 'terminé':
        return 'success';
      case 'en attente':
        return 'warning';
      case 'annulé':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // PDF Export Function
  const exportFinancesAsPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const warningColor = [245, 158, 11]; // amber
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings && settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Rapport Financier", pageWidth - margin, 17, { align: 'right' });
      
      // Add date and filters
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      
      // Add transactions table
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Transactions Financières", margin, 45);
      
      // Format data for the table
      const transactionsData = filteredTransactions.map(transaction => [
        transaction.reference || 'N/A',
        transaction.date ? new Date(transaction.date).toLocaleDateString('fr-FR') : 'N/A',
        transaction.description || 'N/A',
        transaction.clientName || transaction.fournisseurName || 'N/A',
        formatCurrency ? formatCurrency(transaction.montant) : `${transaction.montant} DZD`,
        transaction.statut || 'N/A'
      ]);
      
      // Generate table
      doc.autoTable({
        startY: 50,
        head: [['Référence', 'Date', 'Description', 'Client/Fournisseur', 'Montant', 'Statut']],
        body: transactionsData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 25 },
          2: { cellWidth: 60 },
          3: { cellWidth: 40 },
          4: { cellWidth: 30 },
          5: { cellWidth: 30 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: (data) => {
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text('TravelIT - Système de Gestion pour Agence de Voyage', pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF
      doc.save('rapport-financier.pdf');
      
      // Success notification
      console.log('Financial report exported to PDF successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <>
      {/* Transaction Dialog */}
      <TransactionDialog
        isOpen={transactionDialogOpen}
        onClose={() => setTransactionDialogOpen(false)}
        transaction={editingTransaction}
        clients={clients}
        reservations={reservations}
        onSave={handleSaveTransaction}
      />
      
    <Box sx={{ p: { xs: 2, md: 3 } }} className="space-y-6">
      {/* Header Section */}
        <Box sx={{ 
        mb: { xs: 3, md: 5 },
        p: { xs: 0, md: 0 }
      }} className="flex justify-between items-center">
        <div>
            <Typography variant="h3" sx={{ 
              fontWeight: 800, 
            color: 'white',
            mb: 1.5,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              letterSpacing: '-0.01em'
            }}>
              Gestion Financière
            </Typography>
            <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.6)',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
            }}>
              Gérez vos finances, suivez les paiements et analysez la rentabilité
            </Typography>
        </div>
            <Button 
              variant="contained"
              startIcon={<PlusIcon className="w-5 h-5" />}
              onClick={handleNewTransaction}
              sx={{ 
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
                '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            },
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)'
              }}
            >
              Nouvelle Transaction
            </Button>
        </Box>
        
        {/* Quick Actions Section */}
        {showQuickActions && (
        <Box sx={{ mb: { xs: 3, md: 4 } }} className="space-y-2">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SpeedIcon sx={{ mr: 1.5, color: '#6366f1', fontSize: { xs: 26, md: 30 } }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Actions Rapides
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)'
                  },
                  height: '100%'
                }} onClick={handleNewTransaction}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{
                        bgcolor: '#6366f1',
                        width: 56,
                        height: 56,
                        mb: 2
                      }}>
                        <PlusIcon className="w-6 h-6" />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        Nouvelle Transaction
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Créer une nouvelle transaction financière
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
                  },
                  height: '100%'
                }} onClick={() => {/* Export logic */}}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{
                        bgcolor: '#10b981',
                        width: 56,
                        height: 56,
                        mb: 2
                      }}>
                        <DocumentArrowDownIcon className="w-6 h-6" />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        Rapport Financier
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Générer un rapport financier complet
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)'
                  },
                  height: '100%'
                }} onClick={() => {}}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Badge 
                        badgeContent={financialSummary.enAttente.count || 0} 
                        color="warning"
                        sx={{ '& .MuiBadge-badge': { fontSize: '0.8rem', fontWeight: 'bold' } }}
                      >
                        <Avatar sx={{
                          bgcolor: '#f59e0b',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <ClockIcon className="w-6 h-6" />
                        </Avatar>
                      </Badge>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1, mt: 2 }}>
                        Paiements en Attente
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Gérer les paiements en attente
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)'
                  },
                  height: '100%'
                }} onClick={() => {}}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{
                        bgcolor: '#8b5cf6',
                        width: 56,
                        height: 56,
                        mb: 2
                      }}>
                        <BarChart className="h-6 w-6" />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        Analyse Financière
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Analyser les performances financières
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
        )}
        
        {/* Key Metrics Section */}
        <Box sx={{ mb: { xs: 3, md: 4 } }} className="space-y-2">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssessmentIcon sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Métriques Financières
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Revenus Totaux</p>
                    <p className="text-2xl font-bold text-white mt-2">{formatCurrency(financialSummary.recettes.total || 0)}</p>
                    <p className="text-white/60 text-xs mt-1">
                      {financialSummary.recettes.count || 0} transactions
                    </p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-700">
                    <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Dépenses Totales</p>
                    <p className="text-2xl font-bold text-white mt-2">{formatCurrency(financialSummary.depenses.total || 0)}</p>
                    <p className="text-white/60 text-xs mt-1">
                      {financialSummary.depenses.count || 0} transactions
                    </p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-red-600 to-red-700">
                    <ArrowTrendingDownIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Bénéfice Net</p>
                    <p className="text-2xl font-bold text-white mt-2">{formatCurrency(financialSummary.benefice.total || 0)}</p>
                    <p className="text-white/60 text-xs mt-1">
                      Résultat financier
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white mt-2">
                      {Math.round(financialSummary.benefice.margin || 0)}%
                    </span>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700">
                    <BanknotesIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">Paiements en Attente</p>
                    <p className="text-2xl font-bold text-white mt-2">{formatCurrency(financialSummary.enAttente.total || 0)}</p>
                    <p className="text-white/60 text-xs mt-1">
                      {financialSummary.enAttente.count || 0} paiements
                    </p>
                  </div>
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-700">
                    <ClockIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        {/* Filtration Rapide */}
        <Box sx={{ mb: { xs: 3, md: 4 } }} className="space-y-2">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FunnelIcon className="w-7 h-7 mr-1.5" style={{ color: '#8b5cf6' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Filtration Rapide
            </Typography>
          </Box>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'all'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ClockIcon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">Toutes</span>
              </button>
              <button
                onClick={() => setActiveTab('recettes')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'recettes'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ArrowTrendingUpIcon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">Recettes</span>
              </button>
              <button
                onClick={() => setActiveTab('depenses')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'depenses'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ArrowTrendingDownIcon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">Dépenses</span>
              </button>
              <button
                onClick={() => setActiveTab('payes')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'payes'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Paid className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">Payés</span>
              </button>
              <button
                onClick={() => setActiveTab('attente')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'attente'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ClockIcon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">En Attente</span>
              </button>
              <button
                onClick={() => setActiveTab('thisMonth')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'thisMonth'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <BarChart className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">Ce Mois</span>
              </button>
            </div>
          </div>
        </Box>
        
        {/* Search and Filters Section */}
        <Box sx={{ mb: { xs: 3, md: 4 } }} className="space-y-2">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SearchIcon sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Recherche de Transactions
            </Typography>
          </Box>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 mb-3"
          >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher une transaction..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      },
                      '& .MuiInputBase-input': {
                        color: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      label="Type"
                      onChange={(e) => setTypeFilter(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                              }
                            }
                          }
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">Tous</MenuItem>
                      <MenuItem value="Recette">Recettes</MenuItem>
                      <MenuItem value="Dépense">Dépenses</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}>Statut</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Statut"
                      onChange={(e) => setStatusFilter(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                              }
                            }
                          }
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">Tous</MenuItem>
                      <MenuItem value="Terminé">Terminé</MenuItem>
                      <MenuItem value="En attente">En attente</MenuItem>
                      <MenuItem value="Annulé">Annulé</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}>Période</InputLabel>
                    <Select
                      value={periodFilter}
                      label="Période"
                      onChange={(e) => setPeriodFilter(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                              }
                            }
                          }
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">Toutes</MenuItem>
                      <MenuItem value="today">Aujourd'hui</MenuItem>
                      <MenuItem value="week">Cette semaine</MenuItem>
                      <MenuItem value="month">Ce mois</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<FilterAltIcon />}
                      sx={{ 
                        borderRadius: 2, 
                        flex: 1,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Filtrer
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        flex: 1,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d97706, #b45309)'
                        },
                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                      }}
                    >
                      Rechercher
                    </Button>
                  </Box>
                </Grid>
              </Grid>
          </motion.div>
        </Box>
        
        {/* Transactions Table Section */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ReceiptLong sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
              Transactions Financières
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* View Toggle Buttons */}
              <Box sx={{ display: 'flex', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: 2, overflow: 'hidden' }}>
                <Button
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('table')}
                  sx={{
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    borderRadius: 0,
                    border: 'none',
                    backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                      border: 'none'
                    }
                  }}
                >
                  <TableViewIcon fontSize="small" />
                </Button>
                <Button
                  variant={viewMode === 'card' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('card')}
                  sx={{
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    borderRadius: 0,
                    border: 'none',
                    backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                      border: 'none'
                    }
                  }}
                >
                  <CardViewIcon fontSize="small" />
                </Button>
              </Box>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => exportFinancesAsPDF()}
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Exporter PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleNewTransaction()}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                  },
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                }}
              >
                Nouvelle Transaction
              </Button>
            </Box>
          </Box>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden relative">
            {/* Table View */}
            {viewMode === 'table' && (
            <>
            {loading ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2, color: 'white' }} />
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Chargement des transactions...</Typography>
              </Box>
            ) : filteredTransactions.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-white font-semibold">Référence</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Description</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Client/Fournisseur</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Montant</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {filteredTransactions.map((transaction) => (
                        <motion.tr
                          key={transaction.id} 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-white/5 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div>
                              <div className="flex items-center space-x-2 font-semibold text-white">
                                <div className={`w-2 h-2 rounded-full ${
                                  transaction.type === 'Recette' ? 'bg-green-400' : 'bg-red-400'
                                }`}></div>
                                {transaction.reference || `TRX${transaction.id}`}
                              </div>
                              <div className="text-xs text-white/70">
                                {transaction.type === 'Recette' ? 'Recette' : 'Dépense'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-semibold text-white">
                                {new Date(transaction.date || transaction.dateTransaction || new Date()).toLocaleDateString('fr-FR')}
                              </div>
                              <div className="text-xs text-white/70">
                                {transaction.methode || 'Virement bancaire'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-semibold text-white">
                                {transaction.description}
                              </div>
                              <div className="text-xs text-white/70">
                                {transaction.categorie || 'Non catégorisé'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-md ${
                                transaction.type === 'Recette' ? 'bg-blue-600' : 'bg-orange-500'
                              }`}>
                                {(transaction.client || transaction.clientNom || transaction.fournisseur || '-')?.split(' ')?.map(n => n?.[0] || '').join('') || 'CL'}
                              </div>
                              <div>
                                <div className="font-semibold text-white">
                                  {transaction.client || transaction.clientNom || transaction.fournisseur || '-'}
                                </div>
                                <div className="text-xs text-white/70">
                                  {transaction.clientEmail || transaction.fournisseurEmail || '-'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className={`font-semibold ${
                                transaction.type === 'Recette' ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {transaction.type === 'Dépense' ? '- ' : ''}
                                {formatCurrency(transaction.montant)}
                              </div>
                              <div className="text-xs text-white/70">
                                {transaction.devise || 'DZD'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <Chip
                              label={transaction.statut || 'En attente'}
                              color={getStatusColor(transaction.statut) || 'default'}
                              size="small"
                              sx={{ borderRadius: 16 }}
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleViewTransaction(transaction)}
                                className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                title="Voir les détails"
                              >
                                <VisibilityIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditTransaction(transaction)}
                                className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                                title="Modifier"
                              >
                                <EditIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteTransaction(transaction)}
                                className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                title="Supprimer"
                              >
                                <DeleteIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                  <div className="text-sm text-white/60">
                    Affichage de 1 à {Math.min(10, filteredTransactions.length)} sur {filteredTransactions.length} transactions
                  </div>
                  <div className="flex space-x-2">
                    <button
                      disabled={true}
                      className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      disabled={filteredTransactions.length <= 10}
                      className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
                {/* <TablePagination
                  sx={{
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: '0.875rem',
                      color: 'text.secondary'
                    },
                    '.MuiTablePagination-select': {
                      borderRadius: 2
                    }
                  }}
                /> */}
              </>
          ) : (
            <div className="p-12 text-center">
              <div className="flex flex-col items-center justify-center">
                <FunnelIcon className="h-16 w-16 text-white/30 mb-4" />
                <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                  Aucune transaction trouvée
                </Typography>
                <Typography variant="body2" sx={{ mb: 4, color: 'rgba(255, 255, 255, 0.6)' }}>
                  Essayez de modifier vos filtres ou créez une nouvelle transaction
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                onClick={handleNewTransaction}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                    },
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                    px: 4,
                    py: 1.5
                  }}
              >
                Nouvelle Transaction
                </Button>
              </div>
            </div>
          )}
            </>
            )}

            {/* Card View */}
            {viewMode === 'card' && (
              <div className="p-6">
                {loading ? (
                  <Box sx={{ p: 8, textAlign: 'center' }}>
                    <CircularProgress size={40} sx={{ mb: 2, color: 'white' }} />
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Chargement des transactions...</Typography>
                  </Box>
                ) : filteredTransactions.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="flex flex-col items-center">
                      <ReceiptLong className="h-16 w-16 text-white/40 mb-4" />
                      <h3 className="text-xl font-medium text-white/60 mb-2">Aucune transaction trouvée</h3>
                      <p className="text-white/40 text-lg">
                        {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
                          ? 'Essayez de modifier vos critères de recherche.' 
                          : 'Commencez par ajouter votre première transaction.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <Grid container spacing={3}>
                    {paginatedTransactions.map((transaction, index) => (
                      <Grid item xs={12} sm={6} lg={4} key={transaction.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card sx={{ 
                            background: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 3,
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 24px rgba(139, 92, 246, 0.2)',
                              background: 'rgba(255, 255, 255, 0.08)'
                            }
                          }}>
                            {/* Transaction Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                              <Avatar
                                sx={{
                                  width: 50,
                                  height: 50,
                                  background: transaction.type === 'recette' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                  fontSize: '1.2rem',
                                  fontWeight: 600,
                                  mr: 2
                                }}
                              >
                                {transaction.type === 'recette' ? '+' : '-'}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6" sx={{ 
                                  color: 'white', 
                                  fontWeight: 600, 
                                  fontSize: '1rem',
                                  lineHeight: 1.2,
                                  mb: 0.5
                                }}>
                                  {transaction.reference || `#${transaction.id}`}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                  {formatDate(transaction.date)}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Transaction Details */}
                            <Box sx={{ mb: 3, flex: 1 }}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.8)', 
                                  fontSize: '0.9rem',
                                  mb: 1
                                }}>
                                  {transaction.description || 'Description non renseignée'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.6)', 
                                  fontSize: '0.85rem',
                                  mr: 1
                                }}>
                                  Montant:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: transaction.type === 'recette' ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)', 
                                  fontSize: '1rem', 
                                  fontWeight: 600 
                                }}>
                                  {transaction.type === 'recette' ? '+' : '-'}{formatCurrency(transaction.montant)}
                                </Typography>
                              </Box>
                              {transaction.clientNom && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                  <Typography variant="body2" sx={{ 
                                    color: 'rgba(255, 255, 255, 0.6)', 
                                    fontSize: '0.85rem',
                                    mr: 1
                                  }}>
                                    Client:
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    color: 'rgba(255, 255, 255, 0.8)', 
                                    fontSize: '0.85rem' 
                                  }}>
                                    {transaction.clientNom}
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.6)', 
                                  fontSize: '0.85rem',
                                  mr: 1
                                }}>
                                  Méthode:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.8)', 
                                  fontSize: '0.85rem' 
                                }}>
                                  {transaction.methodePaiement || 'Non spécifiée'}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Status Badge */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                              <Chip
                                label={transaction.statut || 'En attente'}
                                size="small"
                                sx={{
                                  backgroundColor: transaction.statut === 'Validée' ? 'rgba(34, 197, 94, 0.8)' : 
                                  transaction.statut === 'En attente' ? 'rgba(245, 158, 11, 0.8)' :
                                  transaction.statut === 'Annulée' ? 'rgba(239, 68, 68, 0.8)' : 'rgba(107, 114, 128, 0.8)',
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                              <Chip
                                label={transaction.type === 'recette' ? 'Recette' : 'Dépense'}
                                size="small"
                                sx={{
                                  backgroundColor: transaction.type === 'recette' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)',
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
                              <Tooltip title="Voir les détails">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewTransaction(transaction)}
                                  sx={{
                                    color: 'rgb(34, 197, 94)',
                                    bgcolor: 'rgba(34, 197, 94, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
                                    width: 36,
                                    height: 36
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Modifier">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditTransaction(transaction)}
                                  sx={{
                                    color: 'rgb(245, 158, 11)',
                                    bgcolor: 'rgba(245, 158, 11, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' },
                                    width: 36,
                                    height: 36
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteTransaction(transaction)}
                                  sx={{
                                    color: 'rgb(239, 68, 68)',
                                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                                    width: 36,
                                    height: 36
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </div>
            )}
        </div>
        </motion.div>
    </Box>
  </Box>
  </>
);
};

export default FinancesPage;
